import React, { useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { Formik, FieldArray, ErrorMessage } from 'formik'
import { get } from 'lodash'
import * as Yup from 'yup'

import Api from 'services/Api'
import BaseModal from 'components/Modals/BaseModal'
import { Button, Form, DateTimePicker, Select, Radio, Input, MultiSelect } from 'components/Forms'
import { useProgress } from 'components/Hooks'
import { ScrollableUserList } from 'components/UI'
import { Button as MerinioButton, Card, List, Input as MerinioInput } from 'ui'
import { closeModal } from 'store/actions/modals'
import { refreshCounter } from 'store/actions/refresh'
import { t, currentLang } from 'translations'
import { useAttributes } from 'Api'

const typeOptions = [
  'teams',
  'projects',
  'restrictions',
  'skills',
  'requirements',
  'alerts',
  'departments',
  'hiringData',
  'regionalTags',
  'permissions',
  'positions',
  'status',
  'pay'
]

const getBaseData = type => {
  let shape = {}
  let initialValues = { type }
  switch (type) {
    case 'teams':
      shape.teams = Yup.array().of(Yup.string()).min(1, t('manager.users.MUST_SELECT_ONE'))
      initialValues.teams = []
      break
    case 'projects':
      shape.projects = Yup.array().of(Yup.string()).min(1, t('manager.users.MUST_SELECT_ONE'))
      initialValues.projects = []
      break
    case 'restrictions':
      shape.attributes = Yup.array()
        .of(Yup.object().shape({ attributeId: Yup.string().required(), rank: Yup.number().required(), expirationDate: Yup.string() }))
        .min(1, t('manager.users.MUST_SELECT_ONE'))
      initialValues.attributes = []
      break
    case 'skills':
      shape.attributes = Yup.array().of(
        Yup.object().shape({ attributeId: Yup.string().required(), rank: Yup.number().required(), expirationDate: Yup.string() })
      )
      initialValues.attributes = []
      break
    case 'requirements':
      shape.attributes = Yup.array()
        .of(Yup.object().shape({ attributeId: Yup.string().required(), rank: Yup.number().required(), expirationDate: Yup.string() }))
        .min(1, t('manager.users.MUST_SELECT_ONE'))
      initialValues.attributes = []
      break
    case 'alerts':
      shape.attributes = Yup.array()
        .of(Yup.object().shape({ attributeId: Yup.string().required(), expirationDate: Yup.string() }))
        .min(1, t('manager.users.MUST_SELECT_ONE'))
      initialValues.attributes = []
      break
    case 'departments':
      shape.departments = Yup.array().of(Yup.string()).min(1, t('manager.users.MUST_SELECT_ONE'))
      initialValues.departments = []
      break
    case 'hiringData':
      shape.hiringData = Yup.array()
        .of(Yup.object().shape({ hiringDataId: Yup.string().required(), start: Yup.string(), end: Yup.string() }))
        .min(1, t('manager.users.MUST_SELECT_ONE'))
      initialValues.hiringData = []
      break
    case 'regionalTags':
      shape.regionalTags = Yup.array().of(Yup.string()).min(1, t('manager.users.MUST_SELECT_ONE'))
      initialValues.regionalTags = []
      break
    case 'permissions':
      shape.permissions = Yup.array().of(Yup.string()).min(1, t('manager.users.MUST_SELECT_ONE'))
      initialValues.permissions = []
      break
    case 'positions':
      shape.positions = Yup.array().of(Yup.string()).min(1, t('manager.users.MUST_SELECT_ONE'))
      initialValues.positions = []
      break
    case 'pay':
      shape.amount = Yup.string()
      initialValues.payAmount = 0
      initialValues.payType = 'REGULAR'
      break
    default:
      break
  }

  return [initialValues, shape]
}

const UserBulkEditModal = ({
  closeModal,
  userIds,
  tags,
  teams,
  projects,
  type,
  departments,
  hiringData,
  permissions,
  positions,
  enableAttributesExpiration
}) => {
  const dispatch = useDispatch()
  const [formType, setFormType] = useState(type || '')
  const [isAssignation, setIsAssignation] = useState(true)
  const [initialValues, shape] = getBaseData(formType)
  const [Progress, _progressValues, setProgress] = useProgress()
  const [search, setSearch] = useState('')
  const { data: attributes } = useAttributes(data => data.attributes)

  const BulkArrayOfString = ({ type, resources }) => {
    resources = search
      ? resources.filter(resource => (resource.name || resource[`name${currentLang(true)}`]).toLowerCase().includes(search.toLowerCase()))
      : resources
    return (
      <FieldArray
        name={type}
        render={arrayHelpers => (
          <div>
            <Card maxHeight="20rem" className="my-3">
              <List className=" list-group-flush">
                {resources.map(resource => {
                  const values = arrayHelpers.form.values?.[type]
                  const resourceIsSelected = values?.includes(resource._id)

                  const handleClick = () => {
                    if (resourceIsSelected) arrayHelpers.remove(values?.indexOf(resource._id))
                    else arrayHelpers.push(resource._id)
                  }

                  return (
                    <List.Item key={resource._id} className="d-flex justify-content-between align-items-center cursor-pointer" onClick={handleClick}>
                      {resource.name || resource[`name${currentLang(true)}`]}
                      <MerinioButton
                        type="button"
                        size="small"
                        className="float-right"
                        color={resourceIsSelected ? 'success' : 'secondary'}
                        outlined={!resourceIsSelected}
                      >
                        <i className={`far ${resourceIsSelected ? 'fas fa-check-circle' : 'far fa-circle'}`} /> {t(resourceIsSelected ? 'SELECTED' : 'SELECT')}
                      </MerinioButton>
                    </List.Item>
                  )
                })}
              </List>
            </Card>
            <ErrorMessage name={type} render={msg => <small className="text-danger">{msg}</small>} />
          </div>
        )}
      />
    )
  }

  const BulkArrayOfObject = ({ isAssignation, type, resourceId, resources, enableAttributesExpiration }) => {
    resources = search
      ? resources.filter(resource => (resource.name || resource[`name${currentLang(true)}`]).toLowerCase().includes(search.toLowerCase()))
      : resources
    return (
      <FieldArray
        name={type}
        render={arrayHelpers => (
          <div>
            <Card maxHeight="20rem" className="my-3">
              <List className=" list-group-flush">
                {resources.map(resource => {
                  const values = arrayHelpers.form.values?.[type]
                  const resourceSelectedIndex = values?.findIndex(el => el[resourceId] === resource._id)
                  const defaultValues = type === 'hiringData' ? { start: '', end: '' } : { rank: get(resource, 'levels[0].rank', 1) }

                  const handleClick = () => {
                    if (resourceSelectedIndex >= 0) arrayHelpers.remove(resourceSelectedIndex)
                    else arrayHelpers.push({ [resourceId]: resource._id, ...defaultValues })
                  }

                  return (
                    <List.Item
                      key={resource._id}
                      className={`list-group-item-action${resourceSelectedIndex >= 0 ? '' : ' cursor-pointer'}`}
                      onClick={resourceSelectedIndex >= 0 ? null : handleClick}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        {resource.name || resource[`name${currentLang(true)}`]}
                        <MerinioButton
                          type="button"
                          className={`btn-sm float-right ${resourceSelectedIndex >= 0 ? 'btn-success' : 'btn-outline-secondary'}`}
                          onClick={resourceSelectedIndex >= 0 ? handleClick : null}
                        >
                          <i className={`far ${resourceSelectedIndex >= 0 ? 'fas fa-check-circle' : 'far fa-circle'}`} />{' '}
                          {t(resourceSelectedIndex >= 0 ? 'SELECTED' : 'SELECT')}
                        </MerinioButton>
                      </div>
                      {resourceSelectedIndex >= 0 && isAssignation ? (
                        <div className="mt-2">
                          {type === 'hiringData' ? (
                            <>
                              <DateTimePicker
                                module="manager.users.bulkEdit"
                                name={`hiringData.${resourceSelectedIndex}.start`}
                                translation="HIRING_DATA_START"
                                type="date"
                                isClearable
                              />

                              <DateTimePicker
                                module="manager.users.bulkEdit"
                                name={`hiringData.${resourceSelectedIndex}.end`}
                                translation="HIRING_DATA_END"
                                type="date"
                                isClearable
                              />
                            </>
                          ) : (
                            <>
                              {resource.type !== 'alert' ? (
                                <Select
                                  module="administration.positions.form"
                                  name={`attributes.${resourceSelectedIndex}.rank`}
                                  required
                                  translation="rank"
                                  options={resource.levels.map(({ rank: _id, name }) => ({ _id, name }))}
                                />
                              ) : null}
                              {resource.hasExpirationDate && enableAttributesExpiration ? (
                                <DateTimePicker
                                  module="administration.positions.form"
                                  name={`attributes.${resourceSelectedIndex}.expirationDate`}
                                  required
                                  translation="expirationDate"
                                  type="date"
                                  isClearable
                                />
                              ) : null}
                            </>
                          )}
                        </div>
                      ) : null}
                    </List.Item>
                  )
                })}
              </List>
            </Card>
            <ErrorMessage name={type} render={msg => <small className="text-danger">{msg}</small>} />
          </div>
        )}
      />
    )
  }
  const getForm = type => {
    switch (type) {
      case 'teams':
        return <BulkArrayOfString type={type} resources={teams} isAssignation={isAssignation} />
      case 'projects':
        return <BulkArrayOfString type={type} resources={projects} isAssignation={isAssignation} />
      case 'restrictions':
        return (
          <BulkArrayOfObject
            type="attributes"
            resources={attributes.filter(attribute => attribute.type === 'restriction')}
            resourceId="attributeId"
            isAssignation={isAssignation}
            enableAttributesExpiration={enableAttributesExpiration}
          />
        )
      case 'skills':
        return (
          <BulkArrayOfObject
            type="attributes"
            resources={attributes.filter(attribute => attribute.type === 'skill')}
            resourceId="attributeId"
            isAssignation={isAssignation}
            enableAttributesExpiration={enableAttributesExpiration}
          />
        )
      case 'requirements':
        return (
          <BulkArrayOfObject
            type="attributes"
            resources={attributes.filter(attribute => attribute.type === 'requirement')}
            resourceId="attributeId"
            isAssignation={isAssignation}
            enableAttributesExpiration={enableAttributesExpiration}
            search={search}
          />
        )
      case 'alerts':
        return (
          <BulkArrayOfObject
            type="attributes"
            resources={attributes.filter(attribute => attribute.type === 'alert')}
            resourceId="attributeId"
            isAssignation={isAssignation}
            enableAttributesExpiration={enableAttributesExpiration}
          />
        )
      case 'departments':
        return <BulkArrayOfString type={type} resources={departments} isAssignation={isAssignation} />
      case 'hiringData':
        return <BulkArrayOfObject type={type} resources={hiringData} resourceId="hiringDataId" isAssignation={isAssignation} />
      case 'regionalTags':
        return <BulkArrayOfString type={type} resources={tags.filter(tag => tag.type === 'regional')} isAssignation={isAssignation} />
      case 'permissions':
        return <BulkArrayOfString type={type} resources={permissions} isAssignation={isAssignation} />
      case 'positions':
        return <BulkArrayOfString type={type} resources={positions} isAssignation={isAssignation} />
      case 'pay':
        return (
          <>
            <Radio name="payType" options={['REGULAR', 'OVERTIME']} module="manager.users.bulkEdit" translation="payType" noLabel />
            <Input type="number" name="payAmount" module="manager.users.bulkEdit" />
          </>
        )
      default:
        break
    }
  }

  /**
   * Function to submit the wanted modifications
   * @param {object} values
   */
  const submit = async (values, actions) => {
    let currentUser = 0
    let failedUsers = []
    for (const userId of userIds) {
      setProgress({ userId, width: Math.trunc((++currentUser * 100) / userIds.length), hasFailedUsers: failedUsers.length })
      const { success } = await Api[isAssignation ? 'post' : 'delete'](
        `users/bulk/${['restrictions', 'requirements', 'skills', 'alerts'].includes(formType) ? 'attributes' : formType}`,
        isAssignation ? { ...values, userId, isBulk: true } : { data: { ...values, userId, isBulk: true } }
      )
      if (!success) failedUsers.push(userId)
    }
    setProgress({ width: 100, failedUsers, done: true })
    dispatch(refreshCounter('users'))
    actions.resetForm(initialValues)
    setTimeout(() => setProgress(), 2000)
  }

  return (
    <BaseModal hideButtons title="manager.users.bulkEdit" shouldCloseOnOverlayClick={false} isScrollable>
      <Formik validationSchema={Yup.object().shape(shape)} onSubmit={submit} initialValues={initialValues}>
        {actions => (
          <Form>
            {() => (
              <>
                {/* List of users that will be impacted by the changes */}
                <ScrollableUserList usersIds={userIds} />
                {!type && (
                  <MultiSelect
                    module="manager.users.bulkEdit"
                    name="type"
                    options={typeOptions.map(type => ({
                      value: type,
                      label: t(`manager.users.bulkEdit.${type.toUpperCase()}`)
                    }))}
                    handleChange={type => setFormType(type)}
                  />
                )}
                {formType !== '' ? (
                  <>
                    <h5>{t(`manager.users.bulkEdit.SELECT_${formType.toUpperCase()}`)}</h5>
                    {/* Allows the user to switch between ASSIGNATION and UNASSIGNATION */}
                    {!['status', 'pay'].includes(formType) ? (
                      <ToggleAssignUnassign isAssignation={isAssignation} setIsAssignation={setIsAssignation} isSubmitting={actions.isSubmitting} />
                    ) : null}
                    {/* Form to select which resources to add/remove from the user */}
                    <MerinioInput
                      placeholder={t('manager.users.bulkEdit.SEARCH.PLACEHOLDER')}
                      className="mt-2"
                      name="search"
                      type="text"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                    {getForm(formType)}
                  </>
                ) : null}

                <div className="mt-3">
                  <Button
                    type="submit"
                    text={`manager.users.bulkEdit.${['status', 'pay'].includes(formType) ? 'APPLY' : isAssignation ? 'ASSIGN' : 'UNASSIGN'}`}
                    className="btn btn-primary mr-2"
                    disabled={actions.isSubmitting}
                  />
                  <Button text="actions.CLOSE" className="btn btn-outline-secondary" onClick={() => closeModal()} disabled={actions.isSubmitting} />
                </div>

                {/* Progression of the changes once launched */}
                <Progress />
              </>
            )}
          </Form>
        )}
      </Formik>
    </BaseModal>
  )
}

const mapStateToProps = ({ resources, settings }) => ({
  teams: resources.teams,
  projects: resources.projects,
  departments: resources.departments,
  hiringData: resources.hiringData,
  tags: resources.tags,
  permissions: resources.permissions,
  positions: resources.positions,
  enableAttributesExpiration: settings.enableAttributesExpiration
})
const mapDispatchToProps = dispatch => ({ closeModal: () => dispatch(closeModal()) })

export default connect(mapStateToProps, mapDispatchToProps)(UserBulkEditModal)

const ToggleAssignUnassign = ({ isAssignation, isSubmitting, setIsAssignation }) => (
  <>
    <input id="assign" name="assign" className="m-2" type="radio" checked={isAssignation} onChange={() => setIsAssignation(true)} disabled={isSubmitting} />
    <label htmlFor="assign" className="overflow-wrap-anywhere mb-0">
      {t('manager.users.bulkEdit.ASSIGN')}
    </label>
    <input
      id="unassign"
      name="unassign"
      className="m-2"
      type="radio"
      checked={!isAssignation}
      onChange={() => setIsAssignation(false)}
      disabled={isSubmitting}
    />
    <label htmlFor="unassign" className="overflow-wrap-anywhere mb-0">
      {t('manager.users.bulkEdit.UNASSIGN')}
    </label>
  </>
)
